/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useEffect, useCallback, useState } from 'react';
import { Box, InputAdornment, TextField, Typography, Autocomplete } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { IRole, IRoleAssignments } from '../../models/Roles/RolesContracts';
import { getAllRoles, assignRoleToSubject, getRoleOfSubject, deleteRoleFromSubject } from '../../services/roles';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { useStyles } from './styles';
import { RenderChips } from './RenderChips';
import Loader from '../Loader';
import SnackbarAlert from '../SnackbarAlert';
import { useSelector, useDispatch } from 'react-redux';
import { selectAllAssignedPermissions } from '../../store/auth/authSelector';
import { checkPermission } from '../../shared/CheckPermissions/CheckPermission';
import { permissions } from '../../shared/CheckPermissions/constants';
import interpolateString from '../../utility/stringInterpolation';
import { ACCESS_ROLES, ACCESS_SUBJECT_ROLES, ASSIGN_ROLES, CUSTOMER_IDP, DELETE_ROLES, ROLES } from './constants';
import { getRoleOfSubjectAction } from '../../store/role/roleActionCreator';
import { setIdpRoles } from '../../store/user/userActionCreator';
import { selectRoleOfSubject } from '../../store/role/roleSelector';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../../container/Machines/constants';

import { selectShowHideRoleAssignmentLoader } from '../../store/loader/loaderSelector';
import { showErrorAction } from '../../store/error/errorActionCreator';
import { selectError } from '../../store/error/errorSelector';
import { showHideRoleAssignmentLoaderAction } from '../../store/loader/loaderActionCreator';
import { compareByProperty } from '../../utility/commonUtility';


export const getAutocompleteOptionLabel = (option: IRole) => option.name;
export const getAutocompleteOptionSelected = (option: IRole, value: IRole) => option.name === value.name;

const RoleAssignments = ({ id, subjectId, name, autoCompleteTextPlaceholder }: IRoleAssignments) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const [roleList, setRoleList] = useState<Array<IRole>>([]);
  const [renderChipsRoles, setRenderChipRoles] = useState<Array<IRole>>([]);
  const selectedRole = useSelector(selectRoleOfSubject);
  const showLoaderSelector = useSelector(selectShowHideRoleAssignmentLoader);
  const showError = useSelector(selectError);

  const assignPermission =
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission([permissions.subject.read_assign], assignedPermissions.permissions);

  const getRoles = useCallback(() => {
    dispatch(showHideRoleAssignmentLoaderAction(true));
    getAllRoles()
      .then((response: IRole[]) => {
        setRoleList(response.sort(compareByProperty()));
        dispatch(showHideRoleAssignmentLoaderAction(false));
      })
      .catch(_error => {
        const errorMessage = {
          message: interpolateString([ACCESS_ROLES])
        };
        dispatch(showErrorAction(errorMessage));
        dispatch(showHideRoleAssignmentLoaderAction(false));
      });
  }, [dispatch]);

  const getRolesOfSubject = useCallback(
    (subjectId: string) => {
      dispatch(showHideRoleAssignmentLoaderAction(true));
      getRoleOfSubject(subjectId)
        .then((response: IRole[]) => {
          const liamRoles = response.filter((role: IRole) => role.associatedFrom !== CUSTOMER_IDP);
          const idpRoles = response.filter((role: IRole) => role.associatedFrom === CUSTOMER_IDP);
          setRenderChipRoles(liamRoles);
          dispatch(getRoleOfSubjectAction(liamRoles));
          dispatch(setIdpRoles(idpRoles));
          dispatch(showHideRoleAssignmentLoaderAction(false));
        })
        .catch(_error => {
          if (_error.response && _error.response.status === 404) {
            dispatch(getRoleOfSubjectAction([]));
          } else {
            const errorMessage = {
              message: interpolateString([ACCESS_SUBJECT_ROLES])
            };
            dispatch(showErrorAction(errorMessage));
          }
          dispatch(showHideRoleAssignmentLoaderAction(false));
        });
    },
    [dispatch]
  );


  useEffect(() => {
    dispatch(showErrorAction({ message: '' }));
    getRoles();
    getRolesOfSubject(id ?? subjectId);
  }, [dispatch, getRoles, getRolesOfSubject, id, subjectId]);

  const assignRoles = (_event: any, newValue: any[]) => {
    dispatch(showHideRoleAssignmentLoaderAction(true));
    const newRoleToAssign = newValue.find((option: any) => selectedRole.indexOf(option) === -1);
    if (newRoleToAssign) {
      const newRole = {
        subjectId: subjectId,
        roleId: newRoleToAssign?.id.toString()
      };
      assignRoleToSubject(newRole)
        .then(_response => {
          setRenderChipRoles([...renderChipsRoles, newRoleToAssign]);
          dispatch(getRoleOfSubjectAction([...selectedRole, newRoleToAssign]));
          dispatch(
            setSnackBarMessage({
              ...SNACKBAR_ALERT_DEFAULT_PROPS,
              message: `${newRoleToAssign.name} role assigned to ${name}`,
              severity: 'success'
            })
          );
          dispatch(showHideRoleAssignmentLoaderAction(false));
        })
        .catch(_error => {
          dispatch(
            setSnackBarMessage({
              ...SNACKBAR_ALERT_DEFAULT_PROPS,
              message: interpolateString([ASSIGN_ROLES]),
              severity: 'error'
            })
          );
          const errorMessage = {
            message: interpolateString([ASSIGN_ROLES])
          };
          dispatch(showErrorAction(errorMessage));
          dispatch(showHideRoleAssignmentLoaderAction(false));
        });
    } else {
      dispatch(showHideRoleAssignmentLoaderAction(false));
    }
  };

  const deleteRole = (role: IRole) => {
    dispatch(showHideRoleAssignmentLoaderAction(true));
    deleteRoleFromSubject(subjectId, role.id)
      .then(() => {
        setRenderChipRoles(renderChipsRoles.filter((entry: IRole) => entry !== role));
        dispatch(getRoleOfSubjectAction(selectedRole.filter((entry: IRole) => entry !== role)));
        dispatch(
          setSnackBarMessage({
            ...SNACKBAR_ALERT_DEFAULT_PROPS,
            message: `${role.name} role deleted for ${name}`,
            severity: 'success'
          })
        );
        dispatch(showHideRoleAssignmentLoaderAction(false));
      })
      .catch(_error => {
        dispatch(
          setSnackBarMessage({
            ...SNACKBAR_ALERT_DEFAULT_PROPS,
            message: interpolateString([DELETE_ROLES]),
            severity: 'error'
          })
        );
        const errorMessage = {
          message: interpolateString([DELETE_ROLES])
        };
        dispatch(showErrorAction(errorMessage));
        dispatch(showHideRoleAssignmentLoaderAction(false));
      });
  };
  return (
    <Box className={classes.root}>
      <Typography variant="body1" gutterBottom component="div" fontWeight={500}>
        {ROLES}
      </Typography>
      <Autocomplete
        id="autocomplete"
        autoComplete
        multiple
        value={selectedRole}
        isOptionEqualToValue={getAutocompleteOptionSelected}
        forcePopupIcon={false}
        filterSelectedOptions
        disableClearable
        limitTags={-1}
        loading={showLoaderSelector}
        loadingText="Loading..."
        onChange={assignRoles}
        options={roleList}
        getOptionLabel={getAutocompleteOptionLabel}
        renderTags={() => null}
        size="small"
        disabled={!assignPermission}
        fullWidth={false}
        style={{width: 300}}
        renderInput={params => (
          <TextField
            {...params}
            className={classes.inputMargin}
            variant="standard"
            placeholder={autoCompleteTextPlaceholder}
            disabled={!assignPermission}
            InputProps={{
              ...params.InputProps,
              type: 'search',
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className={classes.searchIcon} />
                </InputAdornment>
              )
            }}
          />
        )}
      />

      {showError && !showLoaderSelector && <ErrorMessage {...showError} />}
      {!showLoaderSelector && (
        <RenderChips selectedRole={renderChipsRoles} deleteRole={deleteRole} disabled={!assignPermission} />
      )}
      {showLoaderSelector && <Loader showLoader={showLoaderSelector} isCenter customClasses={classes.loader} />}
      {!showLoaderSelector && <SnackbarAlert />}
    </Box>
  );
};

export default RoleAssignments;
